import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { MdUpload, MdInfoOutline } from "react-icons/md";
import { IoMdCheckmark } from "react-icons/io";
import { FaRegEdit } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { IoAddCircleOutline } from "react-icons/io5";
import { TbCurrentLocation } from "react-icons/tb";
import styles from "../../../styles/__labProfile.module.scss";
import CountryCodeSelect from "../../../components/country-code-select/CountryCodeSelect";
import {
  DEFAULT_COUNTRY_ISO,
  findCountryByIso,
  splitMobileNumber,
} from "../../../constants/countries";
import defaultAvatar from "../../../assets/images/default_avatar.png";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import DialogReusableMd from "../../../components/dialog-reusable-md/DialogReusableMd";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { toast } from '../../../components/toast';
import axios from "axios";
import { APP_PATH } from "../../../api/backendPath";
import CharCount from "../../../components/char-count/CharCount";
import { BIO_MAX_LENGTH } from "../../../utils/profileValidation";

const WEEK_DAYS = [
  { code: "sun", label: "Sun" },
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
];
const ALL_DAY_CODES = WEEK_DAYS.map((d) => d.code);

const DEFAULT_START = "08:00";
const DEFAULT_END = "17:00";

// Operating hours are unrestricted — a center can be set to open at any time.

// Build a full 7-day schedule from the profile. Prefers the per-day
// `operating_schedule`; falls back to the legacy `operating_days` +
// `operating_hours_start/end` so existing records keep working.
const buildScheduleFromProfile = (profile) => {
  const start = (profile?.operating_hours_start || DEFAULT_START).slice(0, 5);
  const end = (profile?.operating_hours_end || DEFAULT_END).slice(0, 5);
  const savedDays =
    Array.isArray(profile?.operating_days) && profile.operating_days.length > 0
      ? profile.operating_days
      : ALL_DAY_CODES;
  const savedSchedule = Array.isArray(profile?.operating_schedule)
    ? profile.operating_schedule
    : null;

  return WEEK_DAYS.map((d) => {
    if (savedSchedule) {
      const match = savedSchedule.find((s) => s.day === d.code);
      if (match) {
        return {
          day: d.code,
          is_open: !!match.is_open,
          start: (match.start || start).slice(0, 5),
          end: (match.end || end).slice(0, 5),
        };
      }
    }
    return { day: d.code, is_open: savedDays.includes(d.code), start, end };
  });
};

// Format a "HH:MM" 24-hour string to a 12-hour label (e.g. "17:00" -> "5:00 PM").
const formatTime12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = String(hhmm).split(":").map(Number);
  if (isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(isNaN(m) ? 0 : m).padStart(2, "0")} ${period}`;
};

// Derive the legacy fields from the per-day schedule so older consumers
// (patient app, admin panel, etc.) keep working unchanged.
const deriveLegacyFromSchedule = (schedule) => {
  const openDays = schedule.filter((s) => s.is_open);
  const starts = openDays.map((s) => s.start).filter(Boolean).sort();
  const ends = openDays.map((s) => s.end).filter(Boolean).sort();
  return {
    operating_days: openDays.map((s) => s.day),
    operating_hours_start: starts[0] || DEFAULT_START,
    operating_hours_end: ends[ends.length - 1] || DEFAULT_END,
  };
};

// Maps the stored consultation type to a human-friendly badge label.
const CONSULTATION_LABELS = {
  video: "Onsite",
  online: "Onsite",
  onsite: "Home Visit",
  home_visit: "Home Visit",
  "video-onsite": "Onsite & Home Visit",
  video_onsite: "Onsite & Home Visit",
  both: "Onsite & Home Visit",
};
const getConsultationLabel = (type) => {
  if (!type) return null;
  return CONSULTATION_LABELS[String(type).trim().toLowerCase()] || null;
};

const PhysioProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  // Country (ISO) of the registered mobile number, detected from the saved number.
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [mobileNumber, setMobileNumber] = useState("");
  const [profileImage, setProfileImage] = useState(
    () => sessionStorage.getItem("physio_avatar_url") || defaultAvatar
  );
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [centerNameEnglish, setCenterNameEnglish] = useState("");
  const [centerNameArabic, setCenterNameArabic] = useState("");
  // Bilingual "About the center" biography surfaced under Center Information.
  const [bioEnglish, setBioEnglish] = useState("");
  const [bioArabic, setBioArabic] = useState("");

  // ---- Center Information validation (same rules as the doctor profile) ----
  // Both center names are required; each "About the Center" text is at most BIO_MAX_LENGTH
  // characters. A field's message shows once the user has edited / left it, or tried to save.
  const [touchedFields, setTouchedFields] = useState({});
  // Leaving / re-opening edit mode starts without old messages.
  useEffect(() => {
    if (!isEditing) setTouchedFields({});
  }, [isEditing]);
  const touchField = (field) =>
    setTouchedFields((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  const bioTooLong = (value) =>
    value.trim().length > BIO_MAX_LENGTH
      ? `About the center must be at most ${BIO_MAX_LENGTH} characters`
      : "";
  const profileErrors = {
    centerNameEnglish: centerNameEnglish.trim()
      ? ""
      : "Center name (English) is required",
    centerNameArabic: centerNameArabic.trim()
      ? ""
      : "Center name (Arabic) is required",
    bioEnglish: bioTooLong(bioEnglish),
    bioArabic: bioTooLong(bioArabic),
  };
  /** The message to show under `field` now ("" = none). */
  const fieldError = (field) =>
    touchedFields[field] ? profileErrors[field] : "";
  /** Red message under a field, linked to it through `${field}-error`. */
  const renderFieldError = (field) =>
    fieldError(field) ? (
      <small
        id={`${field}-error`}
        role="alert"
        className={styles["field-error"]}
      >
        {fieldError(field)}
      </small>
    ) : null;
  /** Props marking a field as invalid (red border, screen-reader state). */
  const invalidProps = (field) =>
    fieldError(field)
      ? {
          className: styles["field-invalid"],
          "aria-invalid": true,
          "aria-describedby": `${field}-error`,
        }
      : {};

  // Per-day operating schedule: [{ day, is_open, start, end }, ...] for all 7 days.
  const [operatingSchedule, setOperatingSchedule] = useState(() =>
    buildScheduleFromProfile(null)
  );

  const toggleDayOpen = (idx) =>
    setOperatingSchedule((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, is_open: !r.is_open } : r))
    );
  const updateDayTime = (idx, field, value) =>
    setOperatingSchedule((prev) =>
      prev.map((r, i) =>
        i === idx ? { ...r, [field]: value } : r,
      ),
    );
  // Consultation type (read-only; configured by admin) shown as a badge.
  const [consultationType, setConsultationType] = useState(null);

  const fileInputRef = useRef(null);

  // Address state
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: "",
    house_flat_floor_no: "",
    street: "",
    street_arabic: "",
    area_district: "",
    area_district_arabic: "",
    city: "",
    city_arabic: "",
    governorate: "",
    governorate_arabic: "",
    country: "Iraq",
    region_id: "",
    is_default: false,
  });

  // Map state
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [mapCenter, setMapCenter] = useState({ lat: 33.3152, lng: 44.3661 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  // Regions catalog (governorate-level) + the selected region's map bounds, used
  // to bias/restrict the location search to the chosen region.
  const [regions, setRegions] = useState([]);
  const [regionBounds, setRegionBounds] = useState(null);
  // True when the picked location is outside the selected region (shown inline
  // under the search box).
  const [regionMismatch, setRegionMismatch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const mapRef = useRef(null);
  const dropdownRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const loaderOptions = useMemo(() => ({
    googleMapsApiKey: apiKey,
    libraries: ["places"],
    id: "google-map-script-physio",
  }), [apiKey]);

  const { isLoaded } = useJsApiLoader(loaderOptions);

  // Same country list (with flags) as every other mobile number field in the apps.
  const selectedCountryData = findCountryByIso(countryIso);

  const createAxiosInstance = () => {
    const token = sessionStorage.getItem("usr_1a2b3c");
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };

  // Fetch profile on mount
  const fetchProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(APP_PATH.physioProfile);

      if (response.data?.data) {
        const profile = response.data.data;
        setCenterNameEnglish(profile.center_name_english || "");
        setCenterNameArabic(profile.center_name_arabic || "");
        setBioEnglish(profile.bio_english || "");
        setBioArabic(profile.bio_arabic || "");
        setOperatingSchedule(buildScheduleFromProfile(profile));
        setConsultationType(profile.consultation_type || null);

        if (profile.avatar_presigned_url || profile.avatar_url) {
          const url = profile.avatar_presigned_url || profile.avatar_url;
          setProfileImage(url);
          // Keep the header's cache in sync so the avatar doesn't reload/flicker
          // when navigating between pages.
          sessionStorage.setItem("physio_avatar_url", url);
          sessionStorage.setItem("provider_avatar_url", url);
          sessionStorage.setItem("physio_avatar_ts", String(Date.now()));
        }

        if (profile.mobile_number) {
          const saved = splitMobileNumber(profile.mobile_number);
          setCountryIso(saved.country.iso);
          setMobileNumber(saved.national);
        }
      }
    } catch (error) {
      console.warn("Failed to fetch profile:", error);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Save profile
  const handleSave = async () => {
    // Validate first: show every message under its field and focus the first invalid one.
    const invalidFields = Object.keys(profileErrors).filter(
      (field) => profileErrors[field]
    );
    if (invalidFields.length) {
      setTouchedFields(
        Object.fromEntries(Object.keys(profileErrors).map((f) => [f, true]))
      );
      toast.error(profileErrors[invalidFields[0]]);
      document.getElementById(invalidFields[0])?.focus();
      return;
    }

    setSaving(true);
    try {
      const axiosInstance = createAxiosInstance();
      const fullMobile = `${selectedCountryData.code} ${mobileNumber.trim()}`;

      const payload = {
        center_name_english: centerNameEnglish.trim(),
        center_name_arabic: centerNameArabic.trim(),
        mobile_number: fullMobile,
        operating_schedule: operatingSchedule,
        // Keep the legacy fields in sync for backward compatibility.
        ...deriveLegacyFromSchedule(operatingSchedule),
        // Send trimmed biographies; empty strings become null on the backend
        // so we don't persist blank "About" values.
        bio_english: bioEnglish.trim(),
        bio_arabic: bioArabic.trim(),
      };

      const response = await axiosInstance.put(APP_PATH.physioProfile, payload);

      if (response.data?.status) {
        toast.success("Profile updated successfully");
        setIsEditing(false);
      } else {
        toast.error(response.data?.message || "Failed to update profile");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  // Handle avatar upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    try {
      setUploadingAvatar(true);

      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Preview immediately
      setProfileImage(base64Data);

      // Upload to server as JSON (same pattern as lab app)
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const response = await axiosInstance.post(APP_PATH.uploadAvatar, {
        file_data: base64Data,
        filename: file.name,
        mime_type: file.type,
        description: "Physiotherapy center avatar",
      });

      if (response.data?.status && response.data?.data?.s3_key) {
        const s3Key = response.data.data.s3_key;

        // Update profile with new avatar S3 key
        const profileAxios = createAxiosInstance();
        await profileAxios.put(APP_PATH.physioProfile, { avatar_url: s3Key });

        toast.success("Avatar uploaded successfully");

        // Notify header to update avatar
        window.dispatchEvent(new CustomEvent("physio-avatar-updated", { detail: { avatarUrl: base64Data } }));
      } else {
        toast.error(response.data?.message || "Failed to upload avatar");
      }
    } catch (error) {
      toast.error("Failed to upload avatar");
      console.error("Avatar upload error:", error);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveImage = async () => {
    // Optimistically reset the preview
    setProfileImage(defaultAvatar);
    if (fileInputRef.current) fileInputRef.current.value = "";

    try {
      // Persist removal on the backend
      const profileAxios = createAxiosInstance();
      await profileAxios.put(APP_PATH.physioProfile, { avatar_url: null });

      toast.success("Avatar removed");
    } catch (error) {
      toast.error("Failed to remove avatar");
      console.error("Avatar remove error:", error);
    } finally {
      // Clear cached avatar and notify the header to reset, keeping them in sync
      sessionStorage.removeItem("physio_avatar_url");
      sessionStorage.removeItem("provider_avatar_url");
      sessionStorage.removeItem("physio_avatar_ts");
      window.dispatchEvent(
        new CustomEvent("physio-avatar-updated", {
          detail: { avatarUrl: null, removed: true },
        })
      );
    }
  };

  // Address handlers
  const fetchAddresses = useCallback(async () => {
    setAddressLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get("/api/v1/physiotherapist/address");
      if (response.data?.status && response.data.data) {
        setAddresses(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      console.warn("Failed to fetch addresses:", error);
    } finally {
      setAddressLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  // Load the governorate-level regions catalog for the region dropdown.
  useEffect(() => {
    (async () => {
      try {
        const axiosInstance = createAxiosInstance();
        const res = await axiosInstance.get(APP_PATH.regions);
        const list = res?.data?.data || res?.data || [];
        setRegions(Array.isArray(list) ? list.filter((r) => r.is_active !== false) : []);
      } catch (e) {
        // Non-fatal — the dropdown just stays empty if regions can't load.
      }
    })();
  }, []);

  // When a region is picked: store region_id, prefill the governorate (EN+AR)
  // from the catalog, geocode the region to get its map bounds, and clear any
  // previously autofilled location values so stale data doesn't linger.
  const handleRegionChange = (value) => {
    const id = value ? Number(value) : "";
    const region = regions.find((r) => r.id === Number(value));
    setAddressForm((prev) => ({
      ...prev,
      region_id: id,
      house_flat_floor_no: "",
      street: "",
      street_arabic: "",
      area_district: "",
      area_district_arabic: "",
      city: "",
      city_arabic: "",
      governorate: region?.region_name_english || "",
      governorate_arabic: region?.region_name_arabic || "",
    }));
    setMarkerPosition(null);
    setLatitude(null);
    setLongitude(null);
    setLocationSearchQuery("");
    setLocationSuggestions([]);
    setShowSuggestions(false);
    setRegionBounds(null);
    setRegionMismatch(false);
    if (region && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        {
          address: `${region.region_name_english}, Iraq`,
          componentRestrictions: { country: "iq" },
        },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const g = results[0].geometry;
            const bounds = g.bounds || g.viewport;
            if (bounds) setRegionBounds(bounds);
            if (g.location) {
              setMapCenter({ lat: g.location.lat(), lng: g.location.lng() });
            }
          }
        }
      );
    }
  };

  const handleAddAddress = () => {
    setEditingAddressId(null);
    resetAddressForm();
    setShowAddressModal(true);
  };

  const handleEditAddress = (addr) => {
    setEditingAddressId(addr.id);
    setAddressForm({
      name: addr.name || "",
      house_flat_floor_no: addr.house_flat_floor_no || "",
      street: addr.street || "",
      street_arabic: addr.street_arabic || "",
      area_district: addr.area_district || "",
      area_district_arabic: addr.area_district_arabic || "",
      city: addr.city || "",
      city_arabic: addr.city_arabic || "",
      governorate: addr.governorate || "",
      governorate_arabic: addr.governorate_arabic || "",
      country: addr.country || "Iraq",
      region_id: addr.region_id || "",
      is_default: addr.is_default || false,
    });
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) return;
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(`/api/v1/physiotherapist/address/${addressId}`);
      toast.success("Address deleted");
      fetchAddresses();
    } catch (error) {
      toast.error("Failed to delete address");
    }
  };

  // Promote an existing address to be the default (backend unsets the others).
  const handleSetDefaultAddress = async (addr) => {
    // Move the Default badge + highlight to the selected address (and clear it
    // from the rest) in place — keep the existing list order (no reordering).
    setAddresses((prev) =>
      prev.map((a) => ({ ...a, is_default: a.id === addr.id }))
    );
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put(`/api/v1/physiotherapist/address/${addr.id}`, {
        ...addr,
        is_default: true,
      });
      toast.success("Default address updated");
      // Intentionally not refetching here: the only change is is_default, which
      // we've already applied in place, and refetching would reorder the list.
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to set default address");
      // Revert to the server's truth if the update failed.
      fetchAddresses();
    }
  };

  const handleSaveAddress = async () => {
    if (!addressForm.street && !addressForm.city) {
      toast.error("Please fill in at least street and city");
      return;
    }
    setAddressSaving(true);
    try {
      const axiosInstance = createAxiosInstance();
      const payload = { ...addressForm, latitude, longitude };
      if (editingAddressId) {
        await axiosInstance.put(`/api/v1/physiotherapist/address/${editingAddressId}`, payload);
        toast.success("Address updated");
      } else {
        await axiosInstance.post("/api/v1/physiotherapist/address", payload);
        toast.success("Address added");
      }
      setShowAddressModal(false);
      setEditingAddressId(null);
      resetAddressForm();
      fetchAddresses();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save address");
    } finally {
      setAddressSaving(false);
    }
  };

  const resetAddressForm = () => {
    setAddressForm({
      name: "", house_flat_floor_no: "", street: "", street_arabic: "",
      area_district: "", area_district_arabic: "", city: "", city_arabic: "",
      governorate: "", governorate_arabic: "", country: "Iraq", region_id: "",
      is_default: false,
    });
    setLatitude(null);
    setLongitude(null);
    setMarkerPosition(null);
    setLocationSearchQuery("");
    setLocationSuggestions([]);
    setShowSuggestions(false);
    setRegionBounds(null);
    setRegionMismatch(false);
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    // Block picking a point outside the selected region — keep the marker where
    // it was and surface the mismatch (inline + toast).
    if (!isWithinSelectedRegion(e.latLng)) {
      setRegionMismatch(true);
      toast.error(
        "This location is outside the selected region. Please pick an address within the selected region."
      );
      return;
    }
    setRegionMismatch(false);
    setMarkerPosition({ lat, lng });
    setLatitude(lat);
    setLongitude(lng);
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          parseAddressComponents(results[0].address_components);
          fetchArabicAddressComponents({ lat, lng });
        }
      });
    }
  };

  // Marker drag: if dropped outside the selected region, snap it back to its
  // previous position and warn; otherwise accept the new spot and refill.
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if (!isWithinSelectedRegion(e.latLng)) {
      setRegionMismatch(true);
      toast.error(
        "This location is outside the selected region. Please pick an address within the selected region."
      );
      // Snap the marker back to the last valid position (re-render restores it).
      setMarkerPosition((prev) => (prev ? { ...prev } : prev));
      return;
    }
    setRegionMismatch(false);
    setMarkerPosition({ lat, lng });
    setLatitude(lat);
    setLongitude(lng);
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          parseAddressComponents(results[0].address_components);
          fetchArabicAddressComponents({ lat, lng });
        }
      });
    }
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const latLng = window.google ? new window.google.maps.LatLng(lat, lng) : null;
          // If a region is selected and the current location falls outside it,
          // reject it (inline + toast) instead of autofilling out-of-region data.
          if (latLng && !isWithinSelectedRegion(latLng)) {
            setRegionMismatch(true);
            toast.error(
              "Your current location is outside the selected region. Please pick an address within the selected region."
            );
            return;
          }
          setRegionMismatch(false);
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setLatitude(lat);
          setLongitude(lng);
          if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === "OK" && results[0]) {
                parseAddressComponents(results[0].address_components);
                fetchArabicAddressComponents({ lat, lng });
              }
            });
          }
        },
        () => toast.error("Unable to get current location")
      );
    }
  };

  const parseAddressComponents = (components) => {
    const patch = {};
    for (const comp of components) {
      const types = comp.types;
      if (types.includes("street_number") || types.includes("premise")) {
        patch.house_flat_floor_no = comp.long_name;
      } else if (types.includes("route")) {
        patch.street = comp.long_name;
      } else if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
        patch.area_district = comp.long_name;
      } else if (types.includes("locality")) {
        patch.city = comp.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        patch.governorate = comp.long_name;
      } else if (types.includes("country")) {
        patch.country = comp.long_name;
      }
    }
    // Merge (don't clobber) so a following async Arabic autofill can add its fields.
    setAddressForm((prev) => ({ ...prev, ...patch }));
  };

  // The Google Maps JS SDK is loaded in a single language, so to get the Arabic
  // version of the picked location we hit the Geocoding REST API with
  // language=ar and autofill the Arabic address fields. This keeps the address
  // stored in BOTH English and Arabic from one location search. Best-effort: on
  // any failure the English fields are still set.
  const fetchArabicAddressComponents = async ({ placeId, lat, lng }) => {
    if (!apiKey) return;
    const param = placeId
      ? `place_id=${encodeURIComponent(placeId)}`
      : lat != null && lng != null
        ? `latlng=${lat},${lng}`
        : null;
    if (!param) return;
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?${param}&language=ar&key=${apiKey}`
      );
      const json = await res.json();
      const components = json?.results?.[0]?.address_components;
      if (!components) return;
      const patch = {};
      for (const comp of components) {
        const types = comp.types;
        if (types.includes("route")) {
          patch.street_arabic = comp.long_name;
        } else if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
          patch.area_district_arabic = comp.long_name;
        } else if (types.includes("locality")) {
          patch.city_arabic = comp.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          patch.governorate_arabic = comp.long_name;
        }
      }
      if (Object.keys(patch).length > 0) {
        setAddressForm((prev) => ({ ...prev, ...patch }));
      }
    } catch (e) {
      // Non-fatal — Arabic autofill is best-effort.
    }
  };

  // Returns true if the given point falls within the selected region's map
  // bounds. When no region is selected (or its bounds couldn't be resolved),
  // there's no restriction so it returns true. Uses GEOMETRY (not names) so
  // it's language/spelling-agnostic.
  const isWithinSelectedRegion = (latLng) => {
    if (!addressForm.region_id || !regionBounds || !window.google) return true;
    return regionBounds.contains(latLng);
  };

  const handleLocationSearch = (query) => {
    setLocationSearchQuery(query);
    if (!query || query.length < 3 || !isLoaded || !window.google) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: query,
        sessionToken: sessionTokenRef.current,
        componentRestrictions: { country: "iq" },
        // Restrict suggestions to the selected region's bounds (when a region is
        // chosen), so the picked address stays within that governorate.
        ...(regionBounds
          ? { locationRestriction: regionBounds, bounds: regionBounds }
          : {}),
      },
      (predictions, status) => {
        if (status === "OK" && predictions) {
          setLocationSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setLocationSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  };

  const handleSelectSuggestion = (suggestion) => {
    setLocationSearchQuery(suggestion.description);
    setShowSuggestions(false);
    setLocationSuggestions([]);
    sessionTokenRef.current = null;
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setLatitude(lat);
        setLongitude(lng);
        parseAddressComponents(results[0].address_components);
        // Also autofill the Arabic fields from the same place.
        fetchArabicAddressComponents({ placeId: suggestion.place_id, lat, lng });
        // If a region is selected, confirm the picked place is inside it by
        // GEOMETRY (not name matching, which fails for EN/AR/Google spelling
        // differences). If bounds couldn't be resolved, skip rather than warn.
        if (addressForm.region_id && regionBounds) {
          const inside = regionBounds.contains(loc);
          setRegionMismatch(!inside);
          if (!inside) {
            toast.error(
              "This location is outside the selected region. Please pick an address within the selected region."
            );
          }
        } else {
          setRegionMismatch(false);
        }
      }
    });
  };
  return (
    <PhysioDashboardLayout currentPage="physio_setting">
      <div className={styles["labProfile-title"]}>Profile Management</div>

      <div className={styles["labProfile-card"]}>
        <div className={styles["lab-info"]}>
          {/* Header */}
          <div className={styles["lab-info__header"]}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <h2 style={{ margin: 0 }}>Physiotherapy Center Profile</h2>
              {getConsultationLabel(consultationType) && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    background: "#e6f7f7",
                    color: "#099494",
                    border: "1px solid #099494",
                  }}
                >
                  {getConsultationLabel(consultationType)}
                </span>
              )}
            </div>
            {isEditing ? (
              <div className={styles["btn--wrapper"]}>
                <button
                  className={`${styles["btn"]} ${styles["btn--outline-primary"]}`}
                  onClick={() => { setIsEditing(false); fetchProfile(); }}
                >
                  Cancel
                </button>
                <button
                  className={`${styles["btn"]} ${styles["btn--primary"]}`}
                  onClick={handleSave}
                  disabled={saving}
                  style={{ opacity: saving ? 0.6 : 1 }}
                >
                  <IoMdCheckmark size={18} /> {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            ) : (
              <button
                className={styles["lab-info__edit-btn"]}
                onClick={() => setIsEditing(true)}
              >
                <FaRegEdit /> Edit
              </button>
            )}
          </div>

          {/* Profile Photo */}
          <div className={styles["lab-info__profile"]}>
            <div className={styles["lab-info__avatar"]}>
              <img
                src={profileImage}
                alt="Center Logo"
                onError={(e) => { e.target.src = defaultAvatar; }}
              />
            </div>
            <div className={styles["lab-info__photo-actions"]}>
              <button
                className={`${styles["btn"]} ${styles["btn--gray"]}`}
                onClick={handleRemoveImage}
              >
                Remove
              </button>
              <button
                className={`${styles["btn"]} ${styles["btn--primary"]}`}
                onClick={() => fileInputRef.current.click()}
                disabled={uploadingAvatar}
              >
                <MdUpload size={18} /> {uploadingAvatar ? "Uploading..." : "Upload"}
              </button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
            </div>
          </div>

          {/* Guidance banner: encourage a clear, high-resolution profile picture */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              margin: "24px 0",
              padding: "12px 16px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "10px",
              color: "#1e40af",
              fontSize: "13px",
              lineHeight: 1.5,
            }}
          >
            <MdInfoOutline size={20} style={{ flexShrink: 0, marginTop: "1px" }} />
            <span>
              Please upload a <strong>clear, high-resolution</strong> profile
              picture so patients can easily recognize your center. Use a
              well-lit, sharp image (JPEG, PNG or WebP).
            </span>
          </div>

          {/* Form */}
          <div className={styles["lab-info__form"]}>
            <div className={styles["form-group"]}>
              <label htmlFor="centerNameEnglish">
                Center Name (English) <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <input
                id="centerNameEnglish"
                disabled={!isEditing}
                value={centerNameEnglish}
                onChange={(e) => {
                  setCenterNameEnglish(e.target.value);
                  touchField("centerNameEnglish");
                }}
                onBlur={() => touchField("centerNameEnglish")}
                placeholder="Enter center name"
                aria-required="true"
                {...invalidProps("centerNameEnglish")}
              />
              {renderFieldError("centerNameEnglish")}
            </div>

            <div className={styles["form-group"]}>
              <label htmlFor="centerNameArabic">
                Center Name (Arabic) <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <input
                id="centerNameArabic"
                disabled={!isEditing}
                value={centerNameArabic}
                onChange={(e) => {
                  setCenterNameArabic(e.target.value);
                  touchField("centerNameArabic");
                }}
                onBlur={() => touchField("centerNameArabic")}
                placeholder="أدخل اسم المركز"
                style={{ direction: "rtl" }}
                aria-required="true"
                {...invalidProps("centerNameArabic")}
              />
              {renderFieldError("centerNameArabic")}
            </div>

            {/* Mobile Number — placed here (right after the two name inputs)
                so the 3-column grid renders Name EN + Name AR + Mobile on a
                single row. The About the Center textareas that follow both
                use `form-group--full` and wrap to their own rows below. */}
            <div className={styles["form-group"]}>
              <label htmlFor="mobile">Mobile Number</label>
              <div
                className={styles["mobile-input-container"]}
                style={{ background: "#f3f4f6", cursor: "not-allowed" }}
              >
                <CountryCodeSelect
                  className={styles["country-selector"]}
                  value={countryIso}
                  onChange={(country) => setCountryIso(country.iso)}
                  disabled
                />
                <input
                  type="tel"
                  id="mobile"
                  placeholder="770 123 4567"
                  value={mobileNumber}
                  readOnly
                  disabled
                  className={styles["phone-input"]}
                  style={{ background: "transparent", color: "#9ca3af", cursor: "not-allowed" }}
                />
              </div>
            </div>

            {/* Textareas inherit the shared `.form-group textarea` styling
                (see __labProfile.module.scss) so the teal focus border,
                border-radius, padding, and disabled treatment match the
                sibling name inputs — no inline overrides needed. */}
            <div
              className={`${styles["form-group"]} ${styles["form-group--full"]}`}
            >
              <label htmlFor="bioEnglish">About the Center (English)</label>
              <textarea
                id="bioEnglish"
                disabled={!isEditing}
                value={bioEnglish}
                onChange={(e) => {
                  setBioEnglish(e.target.value);
                  touchField("bioEnglish");
                }}
                onBlur={() => touchField("bioEnglish")}
                placeholder="Write a short introduction about the center, therapies offered, and specialties."
                rows={4}
                maxLength={BIO_MAX_LENGTH}
                {...invalidProps("bioEnglish")}
              />
              <div className={styles["field-meta"]}>
                {renderFieldError("bioEnglish")}
                <CharCount id="bioEnglish-count" value={bioEnglish} max={BIO_MAX_LENGTH} />
              </div>
            </div>

            <div
              className={`${styles["form-group"]} ${styles["form-group--full"]}`}
            >
              <label htmlFor="bioArabic">About the Center (Arabic)</label>
              <textarea
                id="bioArabic"
                disabled={!isEditing}
                value={bioArabic}
                onChange={(e) => {
                  setBioArabic(e.target.value);
                  touchField("bioArabic");
                }}
                onBlur={() => touchField("bioArabic")}
                placeholder="اكتب نبذة قصيرة عن المركز والعلاجات المقدمة والتخصصات."
                rows={4}
                dir="rtl"
                maxLength={BIO_MAX_LENGTH}
                {...invalidProps("bioArabic")}
              />
              <div className={styles["field-meta"]}>
                {renderFieldError("bioArabic")}
                <CharCount id="bioArabic-count" value={bioArabic} max={BIO_MAX_LENGTH} />
              </div>
            </div>

            <div className={`${styles["form-group"]} ${styles["form-group--full"]}`}>
              <label>Operating Hours</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "6px" }}>
                {operatingSchedule.map((row, idx) => {
                  const dayLabel =
                    WEEK_DAYS.find((d) => d.code === row.day)?.label || row.day;
                  return (
                    <div
                      key={row.day}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        disabled={!isEditing}
                        onClick={() => toggleDayOpen(idx)}
                        style={{
                          minWidth: "72px",
                          padding: "6px 10px",
                          borderRadius: "999px",
                          border: `1px solid ${row.is_open ? "#099494" : "#d1d5db"}`,
                          background: row.is_open ? "#099494" : "#fff",
                          color: row.is_open ? "#fff" : "#374151",
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: isEditing ? "pointer" : "default",
                          opacity: !isEditing && !row.is_open ? 0.5 : 1,
                        }}
                      >
                        {dayLabel}
                      </button>

                      {row.is_open ? (
                        isEditing ? (
                          <div className={styles["time-range"]}>
                            <input
                              type="time"
                              value={row.start}
                              onChange={(e) => updateDayTime(idx, "start", e.target.value)}
                            />
                            <span>—</span>
                            <input
                              type="time"
                              value={row.end}
                              onChange={(e) => updateDayTime(idx, "end", e.target.value)}
                            />
                          </div>
                        ) : (
                          <span style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                            {formatTime12(row.start)} — {formatTime12(row.end)}
                          </span>
                        )
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>Closed</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {profileLoading && (
        <div style={{ textAlign: "center", padding: "20px", color: "#89899b" }}>
          Loading profile...
        </div>
      )}

      {/* Address Management Section */}
      <div className={styles["labProfile-card"]} style={{ marginTop: "20px" }}>
        <div className={styles["lab-info"]}>
          <div className={styles["lab-info__header"]}>
            <h2>Center Address</h2>
            <button
              className={`${styles["btn"]} ${styles["btn--primary"]}`}
              onClick={handleAddAddress}
            >
              + Add Address
            </button>
          </div>

          {addressLoading ? (
            <div style={{ textAlign: "center", padding: "20px", color: "#89899b" }}>Loading addresses...</div>
          ) : addresses.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px", color: "#89899b" }}>
              <p>No address added yet. Click "Add Address" to add your center location.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px" }}>
              {addresses.map((addr) => (
                <div
                  key={addr.id}
                  style={{
                    border: addr.is_default ? "2px solid #0cbdbd" : "1px solid #e3e3e7",
                    borderRadius: "10px",
                    padding: "14px 18px",
                    position: "relative",
                    background: addr.is_default ? "#f2fbfb" : "#fff",
                  }}
                >
                  {addr.is_default && (
                    <span style={{ position: "absolute", top: "8px", right: "12px", fontSize: "11px", background: "#0cbdbd", color: "#fff", padding: "2px 8px", borderRadius: "10px" }}>
                      Default
                    </span>
                  )}
                  <p style={{ fontWeight: 600, marginBottom: "4px" }}>{addr.name || "Center Address"}</p>
                  <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
                    {[addr.house_flat_floor_no, addr.street, addr.area_district, addr.city, addr.governorate, addr.country].filter(Boolean).join(", ")}
                  </p>
                  <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                    <button
                      style={{ fontSize: "12px", color: "#155DFC", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                      onClick={() => handleEditAddress(addr)}
                    >
                      Edit
                    </button>
                    <button
                      style={{ fontSize: "12px", color: "#e5252a", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                      onClick={() => handleDeleteAddress(addr.id)}
                    >
                      Delete
                    </button>
                    {!addr.is_default && (
                      <button
                        style={{ fontSize: "12px", color: "#099494", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                        onClick={() => handleSetDefaultAddress(addr)}
                      >
                        Set as Default
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Address Modal */}
      <DialogReusableMd
        className={styles["addMultipleAddressModal"]}
        open={showAddressModal}
        onClose={() => { setShowAddressModal(false); setEditingAddressId(null); resetAddressForm(); }}
      >
        <div className={styles["login-form-container"]}>
          <h2 className={styles["addressTitle"]}>
            {editingAddressId ? "Edit Address" : "Add Address"}
          </h2>

          {/* Map Section */}
          <div className={styles["mapWrapper"]}>
            {apiKey && isLoaded ? (
              <>
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "300px", borderRadius: "8px" }}
                  center={mapCenter}
                  zoom={13}
                  onClick={handleMapClick}
                  onLoad={(map) => (mapRef.current = map)}
                >
                  {markerPosition && (
                    <Marker
                      position={markerPosition}
                      draggable
                      onDragEnd={handleMarkerDragEnd}
                    />
                  )}
                </GoogleMap>
                <button
                  type="button"
                  className={styles["current-location-btn"]}
                  onClick={handleGetCurrentLocation}
                >
                  <TbCurrentLocation /> Use Current Location
                </button>
              </>
            ) : (
              <div style={{ height: "200px", background: "#f3f4f6", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "#89899b" }}>
                {!apiKey ? "Google Maps API key not configured" : "Loading map..."}
              </div>
            )}
          </div>

          {/* Address Form */}
          <div className={styles["address-form"]}>
            {/* Region — restricts the location search to the selected region */}
            <div className={styles["form-group"]}>
              <label>Region</label>
              <div
                style={{
                  fontSize: "12px",
                  color: "#0f5b5b",
                  background: "#e7fafa",
                  border: "1px solid #cdefef",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  margin: "2px 0 8px",
                }}
              >
                Selecting a region restricts the map search to that area and autofills the address fields below. Locations outside the selected region aren't allowed.
              </div>
              <select
                value={addressForm.region_id === "" ? "" : String(addressForm.region_id)}
                onChange={(e) => handleRegionChange(e.target.value)}
                className={styles["form-control"]}
              >
                <option value="">Select a region</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.region_name_english}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Search */}
            <div className={styles["form-group"]} style={{ position: "relative" }} ref={dropdownRef}>
              <label>Search Location</label>
              <input
                type="text"
                value={locationSearchQuery}
                onChange={(e) => handleLocationSearch(e.target.value)}
                onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search for a location to autofill address..."
                className={styles["form-control"]}
                autoComplete="off"
                disabled={!isLoaded}
              />
              {showSuggestions && locationSuggestions.length > 0 && (
                <ul className={styles["location-suggestions"]}>
                  {locationSuggestions.map((s) => (
                    <li key={s.place_id} onClick={() => handleSelectSuggestion(s)} className={styles["suggestion-item"]}>
                      {s.description}
                    </li>
                  ))}
                </ul>
              )}
              {regionMismatch && (
                <span style={{ display: "block", marginTop: "6px", color: "#dc2626", fontSize: "13px" }}>
                  This location is outside the selected region. Please pick an address within the selected region.
                </span>
              )}
            </div>

            <div className={styles["form-group"]}>
              <label>Address Name <span style={{ color: "#dc3545" }}>*</span></label>
              <input type="text" value={addressForm.name} onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })} placeholder="e.g., Main Branch" className={styles["form-control"]} />
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>House/Flat/Floor No</label>
                <input type="text" value={addressForm.house_flat_floor_no} onChange={(e) => setAddressForm({ ...addressForm, house_flat_floor_no: e.target.value })} className={styles["form-control"]} />
              </div>
              <div className={styles["form-group"]}>
                <label>Street (English) <span style={{ color: "#dc3545" }}>*</span></label>
                <input type="text" value={addressForm.street} onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })} className={styles["form-control"]} />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>Street (Arabic)</label>
                <input type="text" value={addressForm.street_arabic} onChange={(e) => setAddressForm({ ...addressForm, street_arabic: e.target.value })} className={styles["form-control"]} dir="rtl" />
              </div>
              <div className={styles["form-group"]}>
                <label>Area/District (English) <span style={{ color: "#dc3545" }}>*</span></label>
                <input type="text" value={addressForm.area_district} onChange={(e) => setAddressForm({ ...addressForm, area_district: e.target.value })} className={styles["form-control"]} />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>Area/District (Arabic)</label>
                <input type="text" value={addressForm.area_district_arabic} onChange={(e) => setAddressForm({ ...addressForm, area_district_arabic: e.target.value })} className={styles["form-control"]} dir="rtl" />
              </div>
              <div className={styles["form-group"]}>
                <label>City (English) <span style={{ color: "#dc3545" }}>*</span></label>
                <input type="text" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} className={styles["form-control"]} />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>City (Arabic)</label>
                <input type="text" value={addressForm.city_arabic} onChange={(e) => setAddressForm({ ...addressForm, city_arabic: e.target.value })} className={styles["form-control"]} dir="rtl" />
              </div>
              <div className={styles["form-group"]}>
                <label>Governorate (English) <span style={{ color: "#dc3545" }}>*</span></label>
                <input type="text" value={addressForm.governorate} onChange={(e) => setAddressForm({ ...addressForm, governorate: e.target.value })} className={styles["form-control"]} />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>Governorate (Arabic)</label>
                <input type="text" value={addressForm.governorate_arabic} onChange={(e) => setAddressForm({ ...addressForm, governorate_arabic: e.target.value })} className={styles["form-control"]} dir="rtl" />
              </div>
            </div>

            <div className={styles["form-group"]}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={addressForm.is_default}
                  onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
                  style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#099494" }}
                />
                Set as default address
              </label>
            </div>

            <div className={styles["form-actions"]}>
              <button type="button" className={styles["btn-cancel"]} onClick={() => { setShowAddressModal(false); setEditingAddressId(null); resetAddressForm(); }}>
                Cancel
              </button>
              <button type="button" className={styles["btn-save"]} onClick={handleSaveAddress} disabled={addressSaving}>
                {addressSaving ? "Saving..." : editingAddressId ? "Update Address" : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      </DialogReusableMd>
    </PhysioDashboardLayout>
  );
};

export default PhysioProfile;
